/**
 * FacilitySummaryWidget — Dashboard-Kachel „Haustechnik":
 * offene Schadensmeldungen/Aufgaben nach Dringlichkeit + Top-Liste.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Wrench, ArrowRight, MapPin, Clock, AlertTriangle } from 'lucide-react';
import { facilityAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { URGENCY_LABELS, URGENCY_STYLES, STATUS_LABELS, STATUS_STYLES } from '../facility/constants';

const timeStr = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const FacilitySummaryWidget = () => {
  const { hasPermission, isAdmin } = useAuth();
  const canView = isAdmin || hasPermission('facility', 'view');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(canView);

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    try {
      const r = await facilityAPI.dashboardSummary();
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) setData(null);
    } finally { setLoading(false); }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  if (!canView) return null;
  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader className="border-b border-border/40 pb-4"><Skeleton className="h-6 w-48" /></CardHeader>
        <CardContent className="p-4 space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></CardContent>
      </Card>
    );
  }
  if (!data || data.total === 0) return null;

  const urgencyOrder = ['danger', 'high', 'medium', 'low'];

  return (
    <Card className="border-border/60" data-testid="facility-summary-widget">
      <CardHeader className="border-b border-border/40 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle className="font-heading text-lg flex flex-wrap items-center gap-2">
          <Wrench className="h-5 w-5 text-primary shrink-0" />
          Haustechnik
          <Badge variant="destructive" data-testid="facility-widget-total">{data.total} offen</Badge>
          {data.reported_count > 0 && (
            <Badge variant="outline" className="text-amber-700 border-amber-300">{data.reported_count} zu prüfen</Badge>
          )}
        </CardTitle>
        <Link to="/haustechnik" className="shrink-0 self-start sm:self-auto">
          <Button variant="ghost" size="sm" className="text-xs whitespace-nowrap" data-testid="facility-widget-view-all">
            Zur Haustechnik <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* Dringlichkeits-Verteilung */}
        <div className="flex flex-wrap gap-2" data-testid="facility-widget-urgency">
          {urgencyOrder.filter((u) => data.by_urgency[u] > 0).map((u) => (
            <Badge key={u} variant="outline" className={`${URGENCY_STYLES[u]}`}>
              {u === 'danger' && <AlertTriangle className="h-3 w-3 mr-1" />}
              {URGENCY_LABELS[u]}: {data.by_urgency[u]}
            </Badge>
          ))}
        </div>

        {/* Top-Liste (dringendste zuerst) */}
        <ul className="space-y-1.5" data-testid="facility-widget-items">
          {data.items.map((it) => (
            <li key={it.id}>
              <Link to="/haustechnik" className="flex items-center justify-between gap-2 text-sm border-b border-border/40 pb-1.5 hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors">
                <div className="min-w-0">
                  <p className="font-medium truncate">{it.title}</p>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                    {it.location_name && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{it.location_name}</span>}
                    {it.scheduled_start && <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{timeStr(it.scheduled_start)}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="outline" className={`text-[10px] ${URGENCY_STYLES[it.urgency]}`}>{URGENCY_LABELS[it.urgency]}</Badge>
                  <Badge className={`text-[10px] ${STATUS_STYLES[it.status]}`}>{STATUS_LABELS[it.status]}</Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {data.total > data.items.length && (
          <p className="text-xs text-muted-foreground">+ {data.total - data.items.length} weitere offene Aufgaben</p>
        )}
      </CardContent>
    </Card>
  );
};
